/**
 * Runs to the organisation's observability (docs/plans/2026-10-hermes-adoption.md, item 4). AIOE's
 * own contract; the workbench needs nothing (it already reports every run).
 *
 * Every run the platform's ledger holds becomes one OTLP trace at the organisation's destination: the
 * run as the root span, each model call and each tool call a child. The content rule says what words
 * go: `metadata` none at all; `redacted` what the run carries under the workbench's own content rule;
 * `full` the same, only when the organisation says so.
 *
 *   GET  /admin/v1/runs/forwarding        administrators: the destination (never its secret) and its standing
 *   PUT  /admin/v1/runs/forwarding        administrators (Cloud: owners): otlp | langfuse | off
 *   POST /admin/v1/runs/forwarding/test   administrators (Cloud: owners): one test trace, the answer in words
 */
import { z } from "zod";
export const RUN_FORWARD_KINDS = ["off", "otlp", "langfuse"];
export const runForwardKindSchema = z.enum(RUN_FORWARD_KINDS);
export const RUN_FORWARD_CONTENTS = ["metadata", "redacted", "full"];
export const runForwardContentSchema = z.enum(RUN_FORWARD_CONTENTS);
const httpsUrl = z.string().trim().url().max(500);
const secret = z.string().min(1).max(4000);
/**
 * `PUT /admin/v1/runs/forwarding`. A secret (the OTLP headers, Langfuse's secret key) left out keeps the
 * one stored; a new destination needs it. `start`: `now` (runs that end from now on, the default) or
 * `beginning` (every run the ledger still holds first).
 */
export const runForwardingPutSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("off") }).strict(),
    z
        .object({
        kind: z.literal("otlp"),
        /** The OTLP/HTTP traces endpoint; with no path, /v1/traces is added. */
        url: httpsUrl,
        /** Headers the endpoint needs (an Authorization, an api key): kept sealed, shown by name only. */
        headers: z.record(z.string().trim().min(1).max(100), z.string().max(4000)).optional(),
        content: runForwardContentSchema.optional(),
        start: z.enum(["now", "beginning"]).optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("langfuse"),
        /** Langfuse's base address (https://cloud.langfuse.com, or your own). */
        url: httpsUrl,
        publicKey: z.string().trim().min(1).max(200),
        secretKey: secret.optional(),
        content: runForwardContentSchema.optional(),
        start: z.enum(["now", "beginning"]).optional(),
    })
        .strict(),
]);
export const RUN_FORWARD_KIND_TEXT = { off: "Off", otlp: "OpenTelemetry (OTLP/HTTP)", langfuse: "Langfuse" };
export const RUN_FORWARD_CONTENT_TEXT = { metadata: "Metadata only: no prompts, answers or tool arguments", redacted: "What the run carries under the workbench's content rule", full: "Everything the run carries" };
//# sourceMappingURL=runForwarding.js.map